import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useTheme } from 'styled-components';
import Chart from "react-apexcharts";
import { useHistory } from 'react-router-dom';

import { animations } from '../../../../resources';


import {
  Container,
  ChartContainer,
  NoContentWrapper,
} from './styles';

import { IState } from '../../../../store';
import { IWallet } from '../../../../store/modules/wallet/types';
import { ITesouroWallet } from '../../../../store/modules/tesouroWallet/types';
import { PersonalizedWallet } from '../../../../store/modules/personalized_wallets/types';
import formatCurrency from '../../../../utils/formatCurrency';
import getPallete from '../../../../utils/colors';
import Button from '../../../../components/Button';

interface MoneyBalanceProps {
  wallets: IWallet[];
  tesouro_wallets: ITesouroWallet[];
  personalized_wallets: PersonalizedWallet[];
}

const containerAnimation = {
  ...animations.containerAnimation,
  mounted: {
    ...animations.containerAnimation.mounted,
    transition: {
      ...animations.containerAnimation.mounted.transition,
      duration: undefined,
      staggerChildren: undefined,
      delay: 0.6,
    }
  },
}

const MoneyBalance: React.FC<MoneyBalanceProps> = ({ wallets, tesouro_wallets, personalized_wallets }) =>  {
  const theme = useTheme();
  const history = useHistory();

  const handleGoToCreateWallet = () => {
    history.push('/dashboard/create-wallet');
  }

  const pieChartDataLabels = useMemo(() => {
    const labels = [] as string[];

    wallets.forEach(wallet => { labels.push(wallet.name) });
    tesouro_wallets.forEach(tesouro_wallet => { labels.push(tesouro_wallet.name) });
    personalized_wallets.forEach(personalized_wallet => { labels.push(personalized_wallet.name) });

    return labels;
  }, [wallets, tesouro_wallets, personalized_wallets]);

  const pieChartColors = useMemo(() => {
    let stockWalletsAmount = wallets.length;
    let tesouroWalletsAmount = tesouro_wallets.length;
    let personalizedWalletsAmount = personalized_wallets.length;

    const stockWalletColors = getPallete(theme.colors.secondary, theme.colors.primaryDark, stockWalletsAmount);
    const tesouroWalletColors = getPallete('#27187E', theme.colors.success, tesouroWalletsAmount);
    const personalizedWalletColors = getPallete(theme.colors.lightGreen, theme.colors.yellow, personalizedWalletsAmount);

    let walletColorIndex = 0;
    let tesouroWalletColorIndex = 0;
    let personalizedWalletColorIndex = 0;

    const colors = [] as string[];

    wallets.forEach(() => {
      walletColorIndex += 1;

      colors.push(stockWalletColors[walletColorIndex - 1]);
    });

    tesouro_wallets.forEach(() => {
      tesouroWalletColorIndex += 1;

      colors.push(tesouroWalletColors[tesouroWalletColorIndex - 1]);
    });

    personalized_wallets.forEach(() => {
      personalizedWalletColorIndex += 1;

      colors.push(personalizedWalletColors[personalizedWalletColorIndex - 1]);
    });

    return colors;
  }, [wallets, tesouro_wallets, personalized_wallets, theme.colors.lightGreen, theme.colors.secondary, theme.colors.primaryDark, theme.colors.success, theme.colors.yellow]);

  const pieChartData = useMemo(() => {
    const chartData = [] as number[];

    wallets.forEach(wallet => { chartData.push(wallet.current_wallet_value) });
    tesouro_wallets.forEach(tesouro_wallet => { chartData.push(tesouro_wallet.current_wallet_value) });
    personalized_wallets.forEach(personalized_wallet => { chartData.push(personalized_wallet.current_value * 100) });

    return chartData;
  }, [wallets, tesouro_wallets, personalized_wallets]);

  const chartOptions = useMemo(() => ({
    tooltip: {
      y: {
        formatter: function(val: any) {
          return formatCurrency(val / 100)
        },
        title: {
          formatter: function (seriesName: any) {
            return seriesName;
          },
        },
      }
    },
    labels: pieChartDataLabels,
    legend: {
      position: 'bottom',
      offsetY: 8,
      height: 36,
      show: true,
      labels: {
        colors: [theme.colors.grey],
      }
    },
    states: {
      active: {
        filter: {
          type: 'none'
        },
      },
      hover: {
        filter: {
          type: 'none'
        },
      },
    },
    chart: {
      id: 'main-page-pie-chart',
      events: {
        dataPointSelection: function() {
          // no-op
        },
      }
    },
    colors: pieChartColors,
    stroke: {
      show: false,
    },
    dataLabels: {
      dropShadow: {
        opacity: 0.8
      },
      formatter: (val: any) => {
        return `${Number(val).toFixed(2)} %`;
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
        dataLabels: {
          offset: -5,
        },
        donut: {
          background: 'transparent',
        },
      },
    }
  }), [pieChartColors, pieChartDataLabels, theme.colors.grey]);

  return (
    <Container
      variants={containerAnimation}
    >
      <h1>Balanço Patrimonial</h1>

      <ChartContainer>
        {!!pieChartData.length && (
          <Chart
            options={chartOptions as any}
            series={pieChartData}
            type="donut"
            width={'100%'}
            height={'100%'}
            animations={{ initialAnimation: { enabled: true }}}
          />
        )}
      </ChartContainer>
      {!pieChartData.length && (
        <NoContentWrapper>
          <p>O balanceamento de suas carteiras aparecerão aqui quando criá-las!</p>
          <Button
            onClick={handleGoToCreateWallet}
          >
            Criar Carteira
          </Button>
        </NoContentWrapper>
      )}
    </Container>
  );
};

const mapStateToProps = (state: IState) => ({
  wallets: state.wallet.wallets,
  tesouro_wallets: state.tesouro.tesouro_wallets,
  personalized_wallets: state.personalizedWallet.personalizedWallets,
})

const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(MoneyBalance)
